import { useState, useCallback, useRef, useEffect } from "react";
import { getChatDraft, setChatDraft } from "../../chat-dock/chatDrafts";
import { validImageTypes, validMimeTypes } from "../const/fileTypes";

const createClientTempId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const extractErrorMessage = (error) => error?.response?.data?.message ?? error?.message ?? "Message failed to send";

export const useMessageComposer = ({ messagesMutation, receiver, onMessageSent, buildExtraPayload, draftKey }) => {
  // PlainText
  const [message, setMessage] = useState(() => getChatDraft(draftKey));
  const draftKeyRef = useRef(draftKey);

  useEffect(() => {
    if (draftKeyRef.current === draftKey) {
      setChatDraft(draftKey, message);
      return;
    }

    draftKeyRef.current = draftKey;
    setMessage(getChatDraft(draftKey));
  }, [draftKey, message]);

  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  // Optimistic sends: each own message shows as Pending until the server confirms
  // (removed from here, the real message arrives via refetch/socket) or fails (Error + Retry).
  const [pendingMessages, setPendingMessages] = useState([]);
  const pendingMessagesRef = useRef([]);
  useEffect(() => {
    pendingMessagesRef.current = pendingMessages;
  }, [pendingMessages]);

  const uploadingMessage = pendingMessages.some((m) => m.status === "pending");

  const dispatchPayload = useCallback(
    (payload, meta) => {
      const clientTempId = createClientTempId();
      setPendingMessages((prev) => [
        ...prev,
        { clientTempId, payload, status: "pending", errorMessage: null, createdAt: new Date().toISOString(), ...meta },
      ]);

      messagesMutation.mutate(payload, {
        onSuccess: () => {
          setPendingMessages((prev) => prev.filter((m) => m.clientTempId !== clientTempId));
          onMessageSent?.();
        },
        onError: (error) => {
          setPendingMessages((prev) =>
            prev.map((m) => (m.clientTempId === clientTempId ? { ...m, status: "error", errorMessage: extractErrorMessage(error) } : m)),
          );
        },
      });

      return clientTempId;
    },
    [messagesMutation, onMessageSent],
  );

  const retryPendingMessage = useCallback(
    (clientTempId) => {
      const entry = pendingMessagesRef.current.find((m) => m.clientTempId === clientTempId);
      if (!entry) return;

      setPendingMessages((prev) => prev.map((m) => (m.clientTempId === clientTempId ? { ...m, status: "pending", errorMessage: null } : m)));

      messagesMutation.mutate(entry.payload, {
        onSuccess: () => {
          setPendingMessages((prev) => prev.filter((m) => m.clientTempId !== clientTempId));
          onMessageSent?.();
        },
        onError: (error) => {
          setPendingMessages((prev) =>
            prev.map((m) => (m.clientTempId === clientTempId ? { ...m, status: "error", errorMessage: extractErrorMessage(error) } : m)),
          );
        },
      });
    },
    [messagesMutation, onMessageSent],
  );

  // === Adjuntos ===
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  const openMenu = Boolean(anchorEl);

  const handleAttachClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Seleccionar archivos
  const handleFileClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = validMimeTypes.join(",");

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const filtered = files.filter((file) => validMimeTypes.includes(file.type));

      if (filtered.length === 0) {
        alert("Solo se permiten archivos PDF, Word, Excel, ZIP, TXT o CSV.");
        return;
      }

      setSelectedFiles((prev) => [...prev, ...filtered]);
    };

    input.click();
    setAnchorEl(null);
  };

  // Seleccionar imagenes
  const handleImageClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = validImageTypes.join(",");
    input.multiple = true;

    input.onchange = (e) => {
      const images = Array.from(e.target.files);
      const filtered = images.filter((img) => validImageTypes.includes(img.type));

      if (filtered.length === 0) {
        alert("Solo se permiten imágenes JPG, PNG, GIF o BMP.");
        return;
      }

      setSelectedImages((prev) => [...prev, ...filtered]);
    };

    input.click();
    setAnchorEl(null);
  };

  const handleRemoveFile = (file) => setSelectedFiles((prev) => prev.filter((f) => f !== file));

  const handleRemoveImage = (img) => setSelectedImages((prev) => prev.filter((f) => f !== img));

  const handleDragAndDropImages = (files) => {
    const filtered = files.filter((img) => validImageTypes.includes(img.type));

    if (filtered.length === 0) return;

    setSelectedImages((prev) => [...prev, ...filtered]);
  };

  const handleDragAndDropFiles = (files) => {
    const filtered = files.filter((file) => validMimeTypes.includes(file.type));

    if (filtered.length === 0) return;

    setSelectedFiles((prev) => [...prev, ...filtered]);
  };

  // === Mensaje de texto ===
  const isSendable = message.trim().length > 0 || selectedFiles.length > 0 || selectedImages.length > 0;

  const handleSend = useCallback(
    (e) => {
      e?.preventDefault();
      if (!isSendable) return;

      const allFiles = [...selectedFiles, ...selectedImages];

      // Mensaje de texto puro
      if (message.trim() && allFiles.length === 0) {
        const trimmedMessage = message.trim();

        const payload = {
          type: "plainText",
          name: "Text",
          plainText: trimmedMessage,
          receiver,
          ...(buildExtraPayload ? buildExtraPayload(trimmedMessage) : {}),
        };
        dispatchPayload(payload, { kind: "text", text: trimmedMessage });
        setMessage("");
        return;
      }

      // Archivos
      allFiles.forEach((file) => {
        const payload = {
          name: file.name,
          file,
          receiver,
        };
        dispatchPayload(payload, { kind: "file", fileName: file.name });
      });

      setMessage("");
      setSelectedFiles([]);
      setSelectedImages([]);
    },
    [isSendable, message, selectedFiles, selectedImages, receiver, dispatchPayload, buildExtraPayload]
  );

  // === Grabación ===
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);

      recorder.onstop = async () => {
        clearInterval(timerRef.current);
        setRecordingTime(0);
        setIsRecording(false);

        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "video/webm" });

        const payload = {
          name: file.name,
          file,
          receiver,
        };

        dispatchPayload(payload, { kind: "file", fileName: file.name });
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (err) {
      console.error("Error al iniciar grabación:", err);
    }
  }, [dispatchPayload]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
    }
  }, [isRecording]);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  return {
    // Texto
    message,
    setMessage,
    handleSend,
    isSendable,

    // Grabación
    isRecording,
    recordingTime,
    uploadingMessage,
    startRecording,
    stopRecording,

    // Adjuntos
    anchorEl,
    openMenu,
    handleAttachClick,
    handleMenuClose,
    handleFileClick,
    handleImageClick,
    selectedFiles,
    selectedImages,
    handleRemoveFile,
    handleRemoveImage,

    // Drag and drop
    handleDragAndDropFiles,
    handleDragAndDropImages,

    // Per-message send status (Pending/Error + retry)
    pendingMessages,
    retryPendingMessage,
  };
};
